// Echo Engine — public gameplay-facing Sticker API.
//
// This is the SINGLE documented import surface for gameplay code. Import types
// and the service from here only. Do NOT import from ./internal/* or from any
// scanner/camera/backend module directly.
//
// StickerService is a stage-owned engine service, constructed in stage.ts
// alongside the other services. Gameplay obtains the shared instance from the
// stage rather than constructing its own:
//
//   import Stage from '../lib/services/stage/stage';
//   const stickers = Stage.instance().sticker_service;
//   const result = await stickers.scan();
//   if (result.status === 'scanned') {
//     const save = result.session.save;          // free-form JSON
//     const tag = result.session.tag;            // { id, name } project tag
//     await stickers.updateSave((prev) => ({ ...prev, score: 10 }));
//   }
//
// (createStickerService() remains available for isolated instances such as
// tests and for the owner's private-adapter swap.)
//
// Cancellation: pass an AbortSignal via scan({ signal }). Aborting yields
// { status: 'cancelled' }. Expected failures resolve to { status: 'error' }
// with a typed StickerError; the promise does not reject for those.

import type {
  AdapterScanSession,
  StickerScannerAdapter,
} from './sticker_adapter';
import {
  JsonObject,
  ProjectTag,
  ScanOptions,
  ScanResult,
  StickerCollection,
  StickerCollectionState,
  StickerCode,
  StickerError,
  StickerIdentity,
  StickerSession,
  UNASSIGNED_TAG,
} from './sticker_types';
import { deepCloneJson } from './internal/clone';
import { StubScannerAdapter } from './internal/stub_scanner_adapter';

// Re-export the public contract so gameplay has one import surface.
export type {
  JsonObject,
  JsonPrimitive,
  JsonValue,
  ProjectTag,
  ScanOptions,
  ScanResult,
  StickerCode,
  StickerCodeStatus,
  StickerErrorCode,
  StickerCollection,
  StickerCollectionState,
  StickerIdentity,
  StickerSession,
} from './sticker_types';
export { StickerError, UNASSIGNED_TAG } from './sticker_types';
export type { StickerScannerAdapter } from './sticker_adapter';

const MAX_SAVE_BYTES = 256 * 1024;

type SessionListener = (session: StickerSession | null) => void;

/**
 * Normalize an adapter-supplied tag into a complete {@link ProjectTag}. A
 * missing tag, or one with a blank id/name, resolves non-destructively to the
 * Unassigned default so legacy adapter results always expose a usable tag.
 */
function normalizeTag(tag: ProjectTag | undefined): ProjectTag {
  if (!tag || typeof tag.id !== 'string' || tag.id.trim().length === 0) {
    return { ...UNASSIGNED_TAG };
  }
  const name =
    typeof tag.name === 'string' && tag.name.trim().length > 0
      ? tag.name
      : tag.id;
  return { id: tag.id, name };
}

function isPlainJsonObject(value: unknown): value is JsonObject {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  try {
    JSON.stringify(value);
  } catch {
    return false;
  }
  return true;
}

/**
 * A scanner scene object (the in-scene camera Video). Kept structurally opaque
 * here so this service does not import the full scene/camera/WebGPU graph.
 */
export type ScannerObject = unknown;

/** Minimal scene port used to install the service-owned scanner. */
export interface StickerScene {
  add(object: ScannerObject): void;
}

/** Factory injected by Stage and invoked exactly once by StickerService. */
export type ScannerFactory = () => ScannerObject;

export interface StickerServiceOptions {
  adapter?: StickerScannerAdapter;
  scannerFactory?: ScannerFactory;
}

export class StickerService {
  private readonly adapter: StickerScannerAdapter;
  private session: StickerSession | null = null;
  private newRobotSession: AdapterScanSession | null = null;
  private awaitingExistingRobot = false;
  private listeners = new Set<SessionListener>();
  private readonly scanner: ScannerObject | null;
  private readonly addedScenes = new WeakSet<StickerScene>();
  private restorePromise: Promise<StickerSession | null> | null = null;
  private demoCollecting = false;
  private collectionEpoch = 0;

  /** Resume local ownership/save data before scene activation; no new grant. */
  async restoreCollection(): Promise<StickerSession | null> {
    if (this.session) return this.getSession();
    if (!this.adapter.restoreCollection) return null;
    if (!this.restorePromise) {
      const epoch = this.collectionEpoch;
      this.restorePromise = this.adapter.restoreCollection().then(restored => {
        if (epoch !== this.collectionEpoch) {
          if (restored) void this.adapter.endSession(restored.sessionId);
          return this.getSession();
        }
        // Do not replace a session opened while persistence was loading.
        if (!this.session && restored) {
          this.session = this.publicSession(restored, 'ready');
          this.notify();
        }
        return this.getSession();
      }).finally(() => { if (epoch === this.collectionEpoch) this.restorePromise = null; });
    }
    await this.restorePromise;
    return this.getSession();
  }

  /**
   * Explicit demo shortcut. Only code acquisition and the collection choice
   * are automated: ordinary scan(), commit/link, persistence and notifications
   * remain the same as a real scan. Production adapters need not expose it.
   */
  async collectDemoRobot(
    candidates: readonly StickerCode[],
    signal?: AbortSignal
  ): Promise<ScanResult> {
    if (this.demoCollecting) {
      return { status: 'error', error: new StickerError('scan_failed', 'A demo scan is already running.') };
    }
    this.demoCollecting = true;
    try {
      if (!this.adapter.nextUnusedDemoCode) {
        throw new StickerError('unsupported', 'This scanner does not support demo collection scans.');
      }
      const existing = await this.restoreCollection();
      if (existing && !['ready', 'robot_linked'].includes(existing.collection.state)) {
        throw new StickerError('collection_conflict', 'Finish or cancel the current collection choice first.');
      }
      if (signal?.aborted) return { status: 'cancelled' };
      const code = await this.adapter.nextUnusedDemoCode(candidates);
      if (code === null) {
        throw new StickerError('scan_failed', 'All 24 demo stickers are already collected. No collections were changed.');
      }
      const result = await this.scan({ preferredCode: code, signal });
      if (result.status !== 'scanned') return result;
      // A concurrent tab may have collected the selected code in the meantime.
      // Never silently switch to, or merge, another established collection.
      if (result.session.collection.state !== 'new_robot') {
        if (existing && result.session.saveGameId !== existing.saveGameId) {
          await this.scan({ preferredCode: existing.code });
          throw new StickerError('collection_conflict', 'That demo sticker belongs to another collection.');
        }
        return result;
      }
      if (!existing) {
        return { status: 'scanned', session: await this.startNewCollection() };
      }
      this.addToExistingCollection();
      // Once acquired, finish the commit even if the view closes. Never leave
      // a half-linked robot or replay a camera scan as collection proof.
      return await this.scan({ preferredCode: existing.code });
    } catch (error) {
      return { status: 'error', error: error instanceof StickerError ? error :
        new StickerError('internal', error instanceof Error ? error.message : String(error)) };
    } finally {
      this.demoCollecting = false;
    }
  }

  constructor(options: StickerServiceOptions = {}) {
    this.adapter = options.adapter ?? new StubScannerAdapter();
    this.scanner = options.scannerFactory ? options.scannerFactory() : null;
  }

  /** Add the single service-owned Video scanner to a scene, once per scene. */
  add(scene: StickerScene): void {
    if (this.scanner === null || this.addedScenes.has(scene)) return;
    this.addedScenes.add(scene);
    scene.add(this.scanner);
  }

  /**
   * Bring up scanning and return a usable anonymous session with a save game.
   * Never rejects for expected outcomes — inspect the discriminated result.
   */
  async scan<T extends JsonObject = JsonObject>(
    options: ScanOptions = {}
  ): Promise<ScanResult<T>> {
    if (options.signal?.aborted) {
      return { status: 'cancelled' };
    }
    try {
      const adapterSession = await this.adapter.scan({
        signal: options.signal,
        preferredCode: options.preferredCode,
        requireUsed: this.awaitingExistingRobot,
        excludedCode: this.awaitingExistingRobot
          ? this.newRobotSession?.code
          : undefined,
      });

      if (this.awaitingExistingRobot && this.newRobotSession) {
        if (adapterSession.code === this.newRobotSession.code) {
          throw new StickerError(
            'same_sticker',
            'Scan a different robot that is already in the collection.'
          );
        }
        if (!this.adapter.linkNewCodeToExistingCode) {
          throw new StickerError(
            'unsupported',
            'This scanner does not support linking robot collections.'
          );
        }

        const addedRobot = this.identityFrom(this.newRobotSession);
        const linked = await this.adapter.linkNewCodeToExistingCode(
          this.newRobotSession.sessionId,
          adapterSession.sessionId
        );
        this.newRobotSession = null;
        this.awaitingExistingRobot = false;
        this.session = this.publicSession(linked, 'robot_linked', {
          addedRobot,
        });
      } else {
        this.newRobotSession = adapterSession.isNewCode ? adapterSession : null;
        this.session = this.publicSession(
          adapterSession,
          adapterSession.isNewCode ? 'new_robot' : 'ready'
        );
      }

      this.notify();
      // Return an independent clone so callers cannot mutate internal state.
      return { status: 'scanned', session: this.getSession<T>()! };
    } catch (err) {
      if (err instanceof StickerError) {
        if (err.code === 'cancelled') return { status: 'cancelled' };
        return { status: 'error', error: err };
      }
      const message = err instanceof Error ? err.message : String(err);
      return {
        status: 'error',
        error: new StickerError('internal', message),
      };
    }
  }

  /**
   * Commit the currently scanned new robot as the first member of a collection.
   */
  async startNewCollection<T extends JsonObject = JsonObject>(): Promise<
    StickerSession<T>
  > {
    if (
      !this.session ||
      !this.newRobotSession ||
      this.session.collection.state !== 'new_robot'
    ) {
      throw new StickerError(
        'not_scanning',
        'There is no new robot waiting to start a collection.'
      );
    }

    const committed = this.adapter.commitNewCollection
      ? await this.adapter.commitNewCollection(this.newRobotSession.sessionId)
      : { ...this.newRobotSession, isNewCode: false, status: 'used' as const };

    this.newRobotSession = null;
    this.awaitingExistingRobot = false;
    this.session = this.publicSession(committed, 'ready');
    this.notify();
    return this.getSession<T>()!;
  }

  /**
   * Keep the new robot pending and interpret the next scan as proof of an
   * already-established collection.
   */
  addToExistingCollection<T extends JsonObject = JsonObject>(): StickerSession<T> {
    if (
      !this.session ||
      !this.newRobotSession ||
      this.session.collection.state !== 'new_robot'
    ) {
      throw new StickerError(
        'not_scanning',
        'There is no new robot waiting to be linked.'
      );
    }
    this.awaitingExistingRobot = true;
    this.session = this.publicSession(
      this.newRobotSession,
      'awaiting_existing_robot',
      { pendingRobot: this.identityFrom(this.newRobotSession) }
    );
    this.notify();
    return this.getSession<T>()!;
  }

  /** Cancel linking without activating, deleting, or relinking the new code. */
  cancelCollectionLink(): void {
    if (!this.session || !this.newRobotSession) {
      throw new StickerError(
        'not_scanning',
        'There is no collection link to cancel.'
      );
    }
    this.awaitingExistingRobot = false;
    this.newRobotSession = null;
    this.session = null;
    this.notify();
  }

  /** The current session, or null if not scanning. Save data is cloned. */
  getSession<T extends JsonObject = JsonObject>(): StickerSession<T> | null {
    if (!this.session) return null;
    const snapshot: StickerSession = {
      ...this.session,
      tag: { ...this.session.tag },
      linkedCodes: this.session.linkedCodes.map((linked) => ({
        code: linked.code,
        tag: { ...linked.tag },
      })),
      save: deepCloneJson(this.session.save),
      collection: {
        ...this.session.collection,
        members: this.session.collection.members.map((member) => ({
          code: member.code,
          tag: { ...member.tag },
        })),
        ...(this.session.collection.pendingRobot
          ? {
              pendingRobot: {
                code: this.session.collection.pendingRobot.code,
                tag: { ...this.session.collection.pendingRobot.tag },
              },
            }
          : {}),
        ...(this.session.collection.addedRobot
          ? {
              addedRobot: {
                code: this.session.collection.addedRobot.code,
                tag: { ...this.session.collection.addedRobot.tag },
              },
            }
          : {}),
      },
    };
    return snapshot as StickerSession<T>;
  }

  /** Cloned free-form save data for the current session, or null. */
  getSave<T extends JsonObject = JsonObject>(): T | null {
    if (!this.session) return null;
    return deepCloneJson(this.session.save) as T;
  }

  /**
   * Update free-form save data. Accepts either a full replacement object or an
   * updater function receiving the current (cloned) save. Persists via the
   * adapter and returns the cloned, stored save.
   */
  async updateSave<T extends JsonObject = JsonObject>(
    next: T | ((prev: T) => T)
  ): Promise<T> {
    if (!this.session) {
      throw new StickerError('not_scanning', 'No active sticker session');
    }
    if (
      this.session.collection.state === 'new_robot' ||
      this.session.collection.state === 'awaiting_existing_robot'
    ) {
      throw new StickerError(
        'not_scanning',
        'Choose or finish the robot collection before updating its save.'
      );
    }
    const prev = deepCloneJson(this.session.save) as T;
    const candidate = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;

    if (!isPlainJsonObject(candidate)) {
      throw new StickerError('invalid_save', 'Save data must be a JSON object');
    }
    const serialized = JSON.stringify(candidate);
    if (serialized.length > MAX_SAVE_BYTES) {
      throw new StickerError('invalid_save', 'Save data exceeds size limit');
    }

    const stored = deepCloneJson(candidate);
    await this.adapter.writeSave(this.session.sessionId, stored);
    this.session = {
      ...this.session,
      save: deepCloneJson(stored),
    };
    this.notify();
    return deepCloneJson(this.session.save) as T;
  }

  /** Tagged sticker identities linked to the current session's save. */
  getLinkedCodes(): StickerIdentity[] {
    return this.session
      ? this.session.linkedCodes.map((linked) => ({
          code: linked.code,
          tag: { ...linked.tag },
        }))
      : [];
  }

  /** End the current session and release adapter state. */
  async resetLocalCollection(): Promise<void> {
    const ids = new Set([this.session?.sessionId, this.newRobotSession?.sessionId]);
    this.collectionEpoch++;
    this.session = null;
    this.newRobotSession = null;
    this.awaitingExistingRobot = false;
    this.restorePromise = null;
    this.demoCollecting = false;
    this.notify();
    for (const id of ids) if (id) await this.adapter.endSession(id);
  }

  /** End the current session and release adapter state. */
  async endSession(): Promise<void> {
    if (!this.session) return;
    const id = this.session.sessionId;
    this.session = null;
    this.newRobotSession = null;
    this.awaitingExistingRobot = false;
    this.notify();
    await this.adapter.endSession(id);
  }

  /**
   * Subscribe to session changes (scan / updateSave / endSession). Returns an
   * unsubscribe function. Listener receives the cloned session or null.
   */
  subscribe(listener: SessionListener): () => void {
    this.listeners.add(listener);
    listener(this.getSession());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Fixture sticker codes for demos. Empty for production adapters that do not
   * expose fixtures.
   */
  getDemoStickers(): StickerCode[] {
    return this.adapter.demoFixtures?.() ?? [];
  }

  private notify(): void {
    const snapshot = this.getSession();
    for (const listener of this.listeners) listener(snapshot);
  }

  private identityFrom(session: AdapterScanSession): StickerIdentity {
    return {
      code: session.code,
      tag: normalizeTag(session.tag),
    };
  }

  private publicSession(
    adapterSession: AdapterScanSession,
    state: StickerCollectionState,
    details: Partial<
      Pick<StickerCollection, 'pendingRobot' | 'addedRobot'>
    > = {}
  ): StickerSession {
    const scannedIdentity = this.identityFrom(adapterSession);
    const suppliedMembers = adapterSession.members?.length
      ? adapterSession.members
      : [
          scannedIdentity,
          ...adapterSession.linkedCodes.map((code) => ({
            code,
            tag: { ...UNASSIGNED_TAG },
          })),
        ];
    const members = suppliedMembers.map((member) => ({
      code: member.code,
      tag: normalizeTag(member.tag),
    }));

    return {
      sessionId: adapterSession.sessionId,
      code: adapterSession.code,
      saveGameId: adapterSession.saveGameId,
      status: adapterSession.status,
      tag: scannedIdentity.tag,
      linkedCodes: members
        .filter((member) => member.code !== adapterSession.code)
        .map((member) => ({
          code: member.code,
          tag: { ...member.tag },
        })),
      save: deepCloneJson(adapterSession.save),
      collection: {
        state,
        members,
        ...details,
      },
    };
  }
}

/** Create a StickerService. Defaults to the in-memory stub adapter. */
export function createStickerService(
  options: StickerServiceOptions = {}
): StickerService {
  return new StickerService(options);
}
